import { motion, useInView, animate } from "framer-motion";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import {
  ArrowRight,
  Code,
  Database,
  Shield,
  Radio,
  FolderOpen,
  Copy,
  Check,
  Workflow,
  LayoutDashboard,
} from "lucide-react";
import FeatureCard from "../components/FeatureCard";
import CodeBlock from "../components/CodeBlock";
import AnimatedArchitecture from "../components/AnimatedArchitecture";
import ArchitectureDiagram from "../components/ArchitectureDiagram";
import Footer from "../components/Footer";
import FlameWrap from "../components/FlameWrap";
import { useState, useRef, useEffect } from "react";

const features = [
  {
    icon: Database,
    title: "Database",
    description: "SQLite with 14 field types & dual-DB architecture",
    href: "/docs/data-services/database",
  },
  {
    icon: Shield,
    title: "Authentication",
    description: "Email/Password, OAuth2, OTP, MFA/TOTP built-in",
    href: "/docs/security-auth/authentication",
  },
  {
    icon: Radio,
    title: "Realtime",
    description: "SSE + WebSocket with per-channel subscriptions",
    href: "/docs/realtime/websocket",
  },
  {
    icon: FolderOpen,
    title: "File Storage",
    description: "Local & S3-compatible with protected file tokens",
    href: "/docs/file-storage/upload",
  },
  {
    icon: Workflow,
    title: "JavaScript Hooks",
    description: "Sandboxed JS VM hook system for custom backend logic",
    href: "/docs/javascript-hooks/available-hook-events",
  },
  {
    icon: LayoutDashboard,
    title: "Admin UI & Migrations",
    description: "Built-in React admin dashboard & automated schema migrations",
    href: "/docs/migrations/running-migrations",
  },
];

const stackIcons = [
  { name: "React", slug: "react", color: "61DAFB" },
  { name: "Next.js", slug: "nextdotjs", color: "ffffff" },
  { name: "Vue", slug: "vuedotjs", color: "4FC08D" },
  { name: "Svelte", slug: "svelte", color: "FF3E00" },
  { name: "Node.js", slug: "nodedotjs", color: "5FA04E" },
  { name: "Bun", slug: "bun", color: "fbf0df" },
  { name: "Docker", slug: "docker", color: "2496ED" },
  { name: "TypeScript", slug: "typescript", color: "3178C6" },
];



const installTabs = [
  {
    id: "global-cli",
    label: "Global CLI",
    code: `npm install -g solarch
solarch init my-project
cd my-project
solarch serve --dev`,
    lang: "bash" as const,
  },
  {
    id: "programmatic",
    label: "Programmatic",
    code: `import { createServer } from 'solarch'

const app = createServer({
  port: 8090,
  dataDir: './pb_data',
})

app.start()`,
    lang: "typescript" as const,
  },
  {
    id: "docker",
    label: "Docker",
    code: `docker run -p 8090:8090 \\
  -v $(pwd)/pb_data:/app/pb_data \\
  solarch/solarch:latest`,
    lang: "bash" as const,
  },
];

function InlineCopy({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore
    }
  };

  return (
    <div
      className="group relative inline-flex items-center gap-3 rounded-full border border-black/10 dark:border-white/20 bg-black/5 dark:bg-white/10 px-6 py-3 backdrop-blur-md transition-all hover:bg-black/10 dark:hover:bg-white/20 cursor-pointer shadow-sm"
      onClick={handleCopy}
    >
      <code className="text-sm font-mono text-gray-800 dark:text-white/90">{text}</code>
      <div className="flex h-6 w-6 items-center justify-center rounded-full bg-black/5 dark:bg-white/10 transition-colors group-hover:bg-black/10 dark:group-hover:bg-white/20">
        {copied ? (
          <Check className="h-3.5 w-3.5 text-green-600 dark:text-green-400" />
        ) : (
          <Copy className="h-3.5 w-3.5 text-gray-600 dark:text-white/70" />
        )}
      </div>
    </div>
  );
}

export default function Home() {
  const [activeTab, setActiveTab] = useState(installTabs[0].id);
  const activeInstall = installTabs.find((t) => t.id === activeTab)!;

  const ctaRef = useRef<HTMLElement>(null);
  const ctaInView = useInView(ctaRef, { once: true, amount: 0.3 });
  const [ctaWipe, setCtaWipe] = useState(1);

  useEffect(() => {
    if (ctaInView) {
      animate(1, 0, {
        duration: 1.5,
        ease: "easeOut",
        onUpdate: (latest) => setCtaWipe(latest),
      });
    }
  }, [ctaInView]);

  return (
    <div className="flex min-h-screen flex-col">
      <SEO 
        title="Solarch: TypeScript Backend-as-a-Service"
        description="Open-source, TypeScript-native Backend-as-a-Service (BaaS) in a single npm package. Includes SQLite, Express, WebSocket, auth, file storage, and an admin UI."
        canonical="/"
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Organization",
              "@id": "https://solarch.in/#organization",
              "name": "Xvertere",
              "url": "https://github.com/xvertere-org",
              "logo": "https://solarch.in/solarch-logo.svg",
              "sameAs": [
                "https://github.com/xvertere-org",
                "https://www.linkedin.com/company/xvertere/",
                "https://x.com/xvertere?s=20"
              ]
            },
            {
              "@type": "WebSite",
              "@id": "https://solarch.in/#website",
              "url": "https://solarch.in",
              "name": "Solarch",
              "description": "Solarch is an open-source, TypeScript-native Backend-as-a-Service (BaaS) that packages SQLite, Express, WebSocket, authentication, file storage, and an admin UI into a single npm package.",
              "publisher": {
                "@id": "https://solarch.in/#organization"
              },
              "about": {
                "@id": "https://solarch.in/#software"
              }
            },
            {
              "@type": "SoftwareApplication",
              "@id": "https://solarch.in/#software",
              "name": "Solarch",
              "applicationCategory": "DeveloperApplication",
              "applicationSubCategory": "Backend-as-a-Service",
              "runtimePlatform": "Node.js (>=20.0.0)",
              "programmingLanguage": "TypeScript",
              "downloadUrl": "https://www.npmjs.com/package/solarch",
              "codeRepository": "https://github.com/xvertere-org/Solarch",
              "license": "https://github.com/xvertere-org/Solarch/blob/main/LICENSE",
              "url": "https://solarch.in",
              "sameAs": [
                "https://github.com/xvertere-org/Solarch",
                "https://www.npmjs.com/package/solarch"
              ],
              "author": {
                "@id": "https://solarch.in/#organization"
              },
              "publisher": {
                "@id": "https://solarch.in/#organization"
              },
              "maintainer": {
                "@id": "https://solarch.in/#organization"
              },
              "description": "Solarch is an open-source, TypeScript-native Backend-as-a-Service (BaaS) that packages SQLite, Express, WebSocket, authentication, file storage, and an admin UI into a single npm package."
            }
          ]
        }}
      />

      <main className="flex-1">
        {/* ==================== HERO ==================== */}
        <section className="relative overflow-hidden pt-28 pb-20 lg:pt-36 lg:pb-36 min-h-[700px] flex items-center justify-center">
          {/* Background blurred flow */}
          <div className="absolute inset-x-0 top-0 h-full z-0 pointer-events-none">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1.2, ease: "easeOut" }}
              className="absolute inset-0"
            >
              <img
                src="/hero-bg.webp"
                alt="Hero Background"
                className="absolute inset-0 w-full h-full object-cover object-center opacity-100"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[var(--bg-surface)]/20 to-[var(--bg-surface)]"></div>
            </motion.div>
          </div>

          <div className="relative mx-auto w-full max-w-7xl px-4 lg:px-8 z-10 mt-12">
            <div className="flex flex-col items-start text-left max-w-4xl">

              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.1, ease: [0.32, 0.72, 0, 1] }}
                className="mb-6 font-heading text-5xl font-semibold tracking-tighter leading-[1.1] text-white md:text-7xl lg:text-[5.5rem] [text-shadow:0_2px_10px_rgba(0,0,0,0.3)]"
              >
                Your Entire Backend.<br />
                <span className="text-[#ff9854] italic font-light tracking-tight relative z-10 [text-shadow:0_2px_4px_rgba(0,0,0,0.8),0_0_1px_rgba(0,0,0,1)]">One Package.</span>
              </motion.h1>

              <motion.p
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.25, duration: 0.6 }}
                className="mb-8 max-w-xl text-base text-theme-secondary sm:text-lg"
              >
                The open-source, TypeScript-native Backend-as-a-Service (BaaS) in
                a single npm package. Includes embedded SQLite, Express,
                WebSocket, authentication, file storage, and an admin UI.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.3, ease: [0.32, 0.72, 0, 1] }}
                className="flex flex-col sm:flex-row items-start sm:items-center justify-start gap-6 w-full"
              >
                <div className="p-1 rounded-full bg-theme-surface border border-theme shadow-sm">
                  <Link
                    to="/docs/overview/getting-started"
                    className="group relative flex items-center gap-4 bg-primary text-white px-6 py-3 rounded-full font-semibold transition-transform active:scale-[0.98]"
                  >
                    <span>Get Started</span>
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-black/10 group-hover:translate-x-1 group-hover:-translate-y-[1px] transition-transform duration-300">
                      <ArrowRight className="h-4 w-4" />
                    </div>
                  </Link>
                </div>

                <div className="p-1 rounded-full bg-theme-surface border border-theme shadow-sm">
                  <InlineCopy text="npm i solarch" />
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* ==================== FEATURE SHOWCASE ==================== */}
        <section className="py-20 relative z-10">
          <div className="mx-auto w-full max-w-[100vw] px-4 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="flex flex-col items-center text-center mb-16"
            >
              <span className="pill mb-4">Why Solarch</span>
              <h2 className="mb-6 font-heading text-4xl font-bold text-theme">
                Everything in one <span className="text-primary">import</span>
              </h2>
              <p className="max-w-2xl text-lg text-theme-secondary">
                Batteries-included backend with auth, storage, AI, and
                realtime: no assembly required.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="w-full relative"
            >
              {/* Terminal CodeBlock overlay on the left */}
              <div className="hidden min-[900px]:block absolute left-4 lg:left-8 top-1/2 -translate-y-1/2 w-[300px] lg:w-[340px] xl:w-[400px] z-20">
                <CodeBlock
                  code={`import { Solarch } from 'solarch';

// 1. Initialize your unified backend
const app = new Solarch({
  secret: process.env.SOLARCH_SECRET,
  db: { provider: 'sqlite' }
});

// 2. Define a realtime collection
app.collection('documents', {
  schema: {
    title: 'string',
    content: 'string',
    embedding: 'vector(1536)'
  },
  realtime: true
});

// 3. Start the server
app.start(3000);`}
                  lang="typescript"
                  animatedIntro={true}
                />
              </div>

              <ArchitectureDiagram />
            </motion.div>
          </div>
        </section>

        {/* ==================== STACK COMPATIBILITY ==================== */}
        <section className="py-20">
          <div className="mx-auto max-w-7xl px-4 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="mb-10 text-center"
            >
              {/* Pill removed */}
              <h2 className="mb-3 font-heading text-3xl font-bold text-theme">
                Bring your own frontend
              </h2>
              <p className="mx-auto max-w-xl text-theme-secondary">
                Solarch is framework-agnostic. Works with any frontend, any
                deployment.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="relative w-full overflow-hidden py-4"
            >
              {/* Fade gradients for performance (replacing mask-image) */}
              <div className="pointer-events-none absolute left-0 top-0 z-10 h-full w-24 bg-gradient-to-r from-[#0a0603] to-transparent dark:from-[var(--bg-void)]" />
              <div className="pointer-events-none absolute right-0 top-0 z-10 h-full w-24 bg-gradient-to-l from-[#0a0603] to-transparent dark:from-[var(--bg-void)]" />

              <div className="flex w-max animate-marquee gap-8 py-4">
                {[...stackIcons, ...stackIcons, ...stackIcons, ...stackIcons].map((tech, idx) => (
                  <div
                    key={`${tech.slug}-${idx}`}
                    className="flex items-center gap-3 px-8 transition-transform duration-300 hover:scale-105 shrink-0 cursor-pointer"
                  >
                    <img
                      src={`https://cdn.simpleicons.org/${tech.slug}/${tech.color}`}
                      alt={tech.name}
                      className="h-8 w-8 object-contain"
                      loading="lazy"
                      decoding="async"
                    />
                    <span className="text-xl font-bold font-heading tracking-wide text-white">
                      {tech.name}
                    </span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* ==================== FEATURE GRID ==================== */}
        <section className="py-20">
          <div className="mx-auto max-w-7xl px-4 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="mb-12 text-center"
            >
              <h2 className="mb-3 font-heading text-3xl font-bold text-theme">
                Everything you need
              </h2>
              <p className="mx-auto max-w-xl text-theme-secondary">
                Batteries-included backend with auth, storage, AI, and realtime:
                no assembly required.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 mt-8">
              {features.map((f) => (
                <FeatureCard
                  key={f.title}
                  icon={f.icon}
                  title={f.title}
                  description={f.description}
                  href={f.href}
                />
              ))}
            </div>
          </div>
        </section>

        {/* ==================== QUICK INSTALL ==================== */}
        <section className="py-20">
          <div className="mx-auto max-w-4xl px-4 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="mb-10 text-center"
            >
              <h2 className="mb-3 font-heading text-3xl font-bold text-theme">
                Quick Install
              </h2>
              <p className="mx-auto max-w-xl text-theme-secondary">
                Get running in under 60 seconds. Choose your flavor.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <div className="mb-4 flex items-center gap-1 rounded-lg border border-theme bg-theme-surface p-1">
                {installTabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex-1 rounded-md px-4 py-2 text-sm font-medium transition-all ${activeTab === tab.id
                      ? "bg-primary/10 text-primary"
                      : "text-theme-tertiary hover:text-theme-secondary"
                      }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              <CodeBlock
                key={activeTab}
                code={activeInstall.code}
                lang={activeInstall.lang}
              />
            </motion.div>
          </div>
        </section>



        {/* ==================== ARCHITECTURE DIAGRAM ==================== */}
        <section className="bg-theme-surface/30 py-20">
          <div className="mx-auto max-w-5xl px-4 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="mb-10 text-center"
            >
              <h2 className="mb-3 font-heading text-3xl font-bold text-theme">
                Architecture at a glance
              </h2>
              <p className="mx-auto max-w-xl text-theme-secondary">
                One package. One process. Everything wired together.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <AnimatedArchitecture />
            </motion.div>
          </div>
        </section>

        {/* ==================== CTA BANNER ==================== */}
        <section ref={ctaRef} className="pb-24 pt-48 bg-[#0a0603]">
          <div className="mx-auto max-w-6xl px-4 lg:px-8">
            <FlameWrap
              intensity={0.5}
              height={170}
              spread={8}
              radius={40}
              speed={0.25}
              scale={0.75}
              turbulence={0.5}
              turbulenceScale={0.5}
              turbulenceReach={25}
              sparks={1.5}
              sparkSize={0.35}
              sparkDensity={1}
              sparkSpeed={1}
              rim={2.5}
              melt={4.5}
              distortion={10}
              ember={2}
              scorch={0}
              color={[1.0, 0.35, 0.12]}
              wipe={ctaWipe}
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="relative overflow-hidden rounded-[2rem] border border-theme bg-theme-surface p-10 sm:p-16 shadow-2xl flex flex-col lg:flex-row items-center justify-between gap-12"
              >
                {/* Subtle top edge highlight */}
                <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-primary via-primary/50 to-transparent"></div>

                <div className="text-center lg:text-left max-w-xl">
                  <h2 className="mb-4 font-heading text-3xl sm:text-4xl font-bold text-white tracking-tight">
                    Start building today.
                  </h2>
                  <p className="text-lg text-theme-secondary">
                    One install. One process. Everything you need to ship your next project.
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row items-center gap-4 w-full lg:w-auto shrink-0">
                  <Link
                    to="/docs/overview/getting-started"
                    className="group flex w-full sm:w-auto items-center justify-center gap-3 bg-primary text-white px-8 py-3.5 rounded-full font-semibold transition-transform active:scale-[0.98]"
                  >
                    <span>Get Started</span>
                    <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                  <a
                    href="https://github.com/xvertere-org/Solarch"
                    target="_blank"
                    rel="noreferrer"
                    className="group flex w-full sm:w-auto items-center justify-center gap-3 border border-theme bg-[#0a0603] px-8 py-3.5 text-white rounded-full font-semibold transition-colors hover:border-primary/50"
                  >
                    <Code className="h-4 w-4 text-white/40 group-hover:text-primary transition-colors" />
                    <span>Star on GitHub</span>
                  </a>
                </div>
              </motion.div>
            </FlameWrap>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
